<template>
  <page-container>
    <template v-slot:page-fixed-header>
      <span class="text-h5 font-weight-bold ml-3 full-width page-header"
        >{{ $t("menu.calendar") }}
        <v-btn icon x-small class="mx-2 mb-1" @click="openManual()">
          <v-icon color="#404040">mdi-alert-circle-outline</v-icon>
        </v-btn>
      </span>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <a-btn
            class="mr-3 mt-1"
            color="primaryDark"
            v-bind="attrs"
            v-on="on"
            @click.stop="onExport"
            small
          >
            <v-icon size="16" class="mr-1">mdi-export</v-icon>
            {{ $t("lblExportToICS") }}
          </a-btn>
        </template>
        <span>{{ $t("tipExportToICS") }}</span>
      </v-tooltip>
      <div class="mr-4 my-1">
        <search-input
          v-model="search"
          :label="$t('lblSearchTextCalendar')"
          :labelTooltip="$t('lblSearchTextCalendarTooltip')"
        ></search-input>
      </div>
      <v-tooltip bottom :disabled="!isMeetingCreated">
        <template v-slot:activator="{ on, attrs }">
          <a-btn
            :disabled="!isMeetingCreated"
            class="mr-3 mt-1"
            color="primaryDark"
            v-bind="attrs"
            v-on="on"
            @click.stop="onCreateMeeting"
            small
          >
            <v-icon size="16" class="mr-1">mdi-plus</v-icon>
            {{ $t("CreateMeeting") }}
          </a-btn>
        </template>
        <span>{{ $t("agenda.tipCreateMeeting") }}</span>
      </v-tooltip>
    </template>

    <template v-slot:page-content>
      <v-row>
        <v-col cols="12" sm="12" md="12">
          <ejs-schedule
            :locale="locale"
            class="ejschedule"
            id="Schedule"
            ref="ScheduleObj"
            :currentView.sync="scheduleView"
            :selectedDate.sync="selectedDate"
            :eventSettings="eventSettings"
            :eventRendered="onEventRendered"
            :quickInfoTemplates="quickInfoTemplates"
            :cellClick="onCellClick"
            :cellDoubleClick="onCellDoubleClick"
            :moreEventsClick="moreEventsClick"
            :popupOpen="onPopupOpen"
            :dragStop="onDragStop"
            :allowDragAndDrop="false"
            :workHours="workHours"
            :navigating="onNavigating"
            @renderCell="onRenderCell"
          >
            <e-views>
              <e-view option="Month" :maxEventsPerRow="2"></e-view>
              <e-view option="Year"></e-view>
            </e-views>
            <template v-slot:eventTemplate="{ data }">
              <div style="display: contents">
                <div class="e-time">
                  {{ formatTime(data.StartTime) }}
                </div>
                <div class="e-subject">
                  {{ data.Subject }}; <span>{{ data.Location }}</span>
                </div>
              </div>
            </template>
            <template v-slot:tooltipTemplate="{ data }">
              <div class="tooltip-wrap e-schedule-event-tooltip">
                <div>
                  {{ data.meetingName }}
                </div>
                <div>
                  {{ data.Location }}
                </div>
                <div>
                  {{ data.Description }}
                </div>
                <div>
                  {{ getTimeDetails(data) }}
                </div>
              </div>
            </template>
          </ejs-schedule>
          <meeting
            v-if="dialogMeeting"
            v-model="dialogMeeting"
            :selectedMeeting="meetingFormData"
            :isSeriesEdit="isSeriesEdit"
            :isCopy="isCopyMeeting"
          ></meeting>
        </v-col>
      </v-row>
      <v-row class="mb-3">
        <v-col cols="8" sm="12" md="8">
          <upcoming-meeting-list section="calendar"></upcoming-meeting-list>
        </v-col>
        <v-col cols="4" sm="12" md="4">
          <upcoming-survey-list section="calendar"></upcoming-survey-list>
        </v-col>
      </v-row>
      <quick-info-dialog
        v-if="isIpadQuickInfoView"
        v-model="showIpadInfoDialog"
        :meetingFormData="meetingFormData"
        @editSelection="onEditSelection"
        @copyMeeting="onCopyMeeting"
      ></quick-info-dialog>
      <more-event-popup
        v-if="moreEventData"
        v-model="showMoreEventDialog"
        :data="moreEventData"
        @open="openQuickInfoDevicePopup"
      ></more-event-popup>
      <message-sheet
        :showMessage="displayMessage"
        :message="message"
        :messageType="messageType"
        @close="displayMessage = false"
      ></message-sheet>
      <email-send-confirmation
        :emailData="emailData"
        :notificationMessage="notificationMessage"
        @send="onEmailSend"
        @cancel="onEmailDialogCancel"
      ></email-send-confirmation>
      <meeting-email-notification
        v-if="showEmailDialog"
        v-model="showEmailDialog"
        :meetingObject="meetingObject"
        :isMeetingDeleted="true"
        @send="onEmailSend"
      ></meeting-email-notification>
    </template>
  </page-container>
</template>

<script>
import Vue from "vue";
import {
  SchedulePlugin,
  Day,
  Week,
  WorkWeek,
  Month,
  Agenda,
  Resize,
  DragAndDrop,
  Year,
} from "@syncfusion/ej2-vue-schedule";
import { extend, loadCldr, L10n, setCulture } from "@syncfusion/ej2-base";
import { mapActions, mapGetters, mapState } from "vuex";
import QuickInfoHeader from "./quick-info/QuickInfoHeader.vue";
import QuickInfoContent from "./quick-info/QuickInfoContent.vue";
import QuickInfoFooter from "./quick-info/QuickInfoFooter.vue";
import Meeting from "../meeting.vue";
import MeetingTypeAutocomplete from "../MeetingTypeAutocomplete.vue";
import SearchInput from "../../SearchInput.vue";
import MeetingTypeMenuList from "../MeetingTypeMenuList.vue";
import MeetingTypeChipGroup from "../MeetingTypeChipGroup.vue";
import MeetingTypeFilter from "../MeetingTypeFilter.vue";
import Loader from "../../Loader.vue";
import QuickInfoDialog from "./QuickInfoDialog.vue";
import MoreEventPopup from "./MoreEventPopup.vue";
import customFunctions from "@/codeFiles/taskHelper.js";
loadCldr(
  require("cldr-data/supplemental/numberingSystems.json"),
  require("cldr-data/main/ja/ca-gregorian.json"),
  require("cldr-data/main/ja/numbers.json"),
  require("cldr-data/main/ja/timeZoneNames.json")
);
import * as localeText from "@/Langs/locale.json";
import MessageSheet from "../../MessageSheet.vue";
import SignalRMixin from "../../../mixin/SignalRMixin";
import EmailSendConfirmation from "../EmailSendConfirmation.vue";
import UpcomingMeetingList from "@/components/dashboard/UpcomingMeetiingList.vue";
import UpcomingSurveyList from "@/components/dashboard/UpcomingSurveyList.vue";
import { formatDate } from "../../../utils/util";
import MeetingEmailNotification from "../MeetingEmailNotification.vue";
import { DEFAULT_MEETING_TYPE_COLOR } from "../../../utils/constant";
import tenantConfigService from '../../../api/tenantConfigService';

Vue.use(SchedulePlugin);
L10n.load(localeText.default);

export default {
  name: "CalendarView",
  mixins: [SignalRMixin],
  components: {
    QuickInfoHeader,
    QuickInfoContent,
    Meeting,
    MeetingTypeAutocomplete,
    SearchInput,
    MeetingTypeMenuList,
    MeetingTypeChipGroup,
    MeetingTypeFilter,
    Loader,
    QuickInfoDialog,
    MoreEventPopup,
    MessageSheet,
    EmailSendConfirmation,
    UpcomingMeetingList,
    UpcomingSurveyList,
    MeetingEmailNotification,
  },
  data() {
    return {
      isCopyMeeting: false,
      meetingObject: null,
      showEmailDialog: false,
      manualClick: 0,
      manualWindow: {},
      isSeriesEdit: false,
      emailData: {},
      notificationMessage: "",
      displayMessage: false,
      messageType: "",
      message: "",
      isIpadQuickInfoView: false,
      showIpadInfoDialog: false,
      showMoreEventDialog: false,
      moreEventData: null,
      workHours: { highlight: true },
      startHour: "08:00",
      endHour: "22:00",
      openMeetingTypeMenu: false,
      meetingTypeColorsCache: null,
      search: "",
      dialogMeeting: false,
      meetingFormData: null,
      scheduleView: "Month",
      selectedDate: new Date(),
      updatedMeetingTypeList: [],
      data: ["Week", "Month"],
      quickInfoTemplates: {
        header: function (e) {
          return {
            template: QuickInfoHeader,
          };
        },
        content: function (e) {
          return {
            template: QuickInfoContent,
          };
        },
        footer: function (e) {
          return {
            template: QuickInfoFooter,
          };
        },
      },
    };
  },
  provide: {
    schedule: [Day, Week, WorkWeek, Month, Year, Agenda, Resize, DragAndDrop],
  },
  computed: {
    LoaderText() {
      return this.$t("LoaderText");
    },
    locale() {
      setCulture(this.$i18n.locale);
      return this.$i18n.locale;
    },
    ...gettersMap(),
    ...mapState({
      meetingListData: (state) => state.meeting.defaultMeetingsList,
      userList: (state) => state.people.userList,
    }),
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    meetingTypesList() {
      if (this.updatedMeetingTypeList && this.updatedMeetingTypeList.length > 0) {
        return this.updatedMeetingTypeList;
      }
      return this.meetingTypeStoreList;
    },
    meetingTypeColorsWatcher() {
      // This will be reactive and trigger updates when colors change
      const storageKey = `meetingTypeColors#${this.tenantId}`;
      const currentColors = localStorage.getItem(storageKey);

      // If colors changed, force re-render of meeting data
      if (currentColors !== this.meetingTypeColorsCache) {
        this.meetingTypeColorsCache = currentColors;
        this.$nextTick(() => {
          if (this.$refs.ScheduleObj && this.$refs.ScheduleObj.ej2Instances) {
            this.$refs.ScheduleObj.ej2Instances.refreshEvents();
          }
        });
      }

      return currentColors;
    },
    meetingTypes() {
      return this.meetingTypesList.filter((item) => {
        if (this.$security.checkMeetingTypeAccess(item.id, "CreateMeeting")) {
          return item;
        }
      });
    },
    meetingData() {
      return this.meetingListData.map((meeting) => {
        const meetingTypeObj = this.meetingTypesList.find(
          (c) =>
            String(c.label).toLowerCase() ===
            String(meeting.meetingType).toLowerCase()
        );

        // Get color from meetingTypeObj (API data) or use default
        let color = "#F5CBA7"; // Default color
        if (meetingTypeObj && meetingTypeObj.color) {
          color = meetingTypeObj.color;
        }

        return {
          ...meeting,
          meetingName: meeting.meetingName,
          Location: meeting.location,
          Subject: meeting.meetingName,
          StartTime: new Date(meeting.scheduletStartTimeInUTC),
          EndTime: new Date(meeting.scheduledEndTimeInUTC),
          meetingType: meetingTypeObj || { label: meeting.meetingType, color },
          Description: meeting.meetingType,
        };
      });
    },
    filterMeetingsData() {
      const searchTerm = (this.search || "").toLowerCase();

      const filterData = this.meetingData.filter(
        ({ Subject, Description }) =>
          (Subject || "").toLowerCase().includes(searchTerm) ||
          (Description || "").toLowerCase().includes(searchTerm)
      );

      return filterData;
    },
    eventSettings() {
      return {
        dataSource: extend([], this.filterMeetingsData, null, true),
        enableTooltip: true,
        tooltipTemplate: "tooltipTemplate",
        template: "eventTemplate",
      };
    },
    tenantId() {
      return sessionStorage.getItem("tenantId");
    },
    getMeetingTypesList() {
      return this.meetingTypesList.filter((item) => {
        if (this.$security.checkMeetingTypeAccess(item.id, "CreateMeeting")) {
          return item;
        }
      });
    },
    isMeetingCreated() {
      return Boolean(this.getMeetingTypesList.length);
    },
  },
  created() {
    this.$root.$on("onDeleteMeeting", (params) => {
      this.onDeleteMeeting(params);
      this.isIpadQuickInfoView = false;
    });
  },
  beforeDestroy() {
    this.$root.$off("onDeleteMeeting");
    this.$root.$off("meetingAlert");
    this.$root.$off("loadMeetings");
    this.$root.$off("meetingTypeColorsUpdated");
  },
  async mounted() {
    try {
      this.signalRInit(`meeting_${this.tenantId}`);
      await this.signalRUpdateMeetingColor(`tenant_config_${this.selectedTenant}`);
      const filter = await this.getCurrentMonthYear(this.selectedDate);
      const p1 = this.getMeetingListApi(filter);
      const p2 = this.getAgendaListApi();
      await Promise.all([p1, p2]);
      this.tenantSettingTypes("topiccategory");

      this.$root.$on("meetingAlert", (params) => {
        const { message, messageType } = { ...params };
        this.showMessageSheet(message, messageType);
      });

      this.$root.$on("loadMeetings", async () => {
        const filter = await this.getCurrentMonthYear(this.selectedDate);
        await this.getMeetingListApi(filter);
      });
      this.$root.$on("meetingTypeColorsUpdated", () => {
        this.$nextTick(() => {
          if (this.$refs.ScheduleObj && this.$refs.ScheduleObj.ej2Instances) {
            this.$refs.ScheduleObj.ej2Instances.refreshEvents();
          }
        });
      });
    } catch (error) {
      console.error("Error in mounted:", error);
    }
  },
  methods: {
    ...mapActions({
      getMeetingListApi: "meeting/getDefaultMeetingList",
      updateMeetingData: "meeting/updateMeeting",
      getAgendaListApi: "agenda/getAgendaList",
      deleteMeetingData: "meeting/deleteMeetingForCalendar",
      getMeetingDetails: "meeting/getMeetingById",
      getDeletedUsersList: "meeting/getDeletedUsersListTemp",
      exportIcs: "meeting/exportIcs",
      updateTenantConfig: "tenant/updateTenantConfig",
    }),
    async signalRUpdateMeetingColor(targetArg) {
      try {
        let target = targetArg;
        
        this.signalConnection.on(target, async (res) => {
          
          if (res.config_name === 'meetingType') {
            this.updateMeetingTypeColor()
          }
        });
        
        if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
          await this.signalConnection.start();
        }
      } catch (error) {
        console.error('SignalR connection error:', error);
      }
    },
    getMeetingTypeColor(meetingType) {
      const defaultColor = DEFAULT_MEETING_TYPE_COLOR;

      const label =
        typeof meetingType === "object" && meetingType?.label
          ? meetingType.label
          : typeof meetingType === "string"
          ? meetingType
          : null;

      if (label) {
        // Get meeting type from API data
        const meetingTypeObj = this.meetingTypesList.find(
          (c) => String(c.label).toLowerCase() === String(label).toLowerCase()
        );
        return meetingTypeObj?.color || defaultColor;
      }

      return defaultColor;
    },
    onRenderCell(args) {
      if (args.elementType === "monthCells") {
        const date = new Date(args.date);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const dateHeaderWrap = args.element.querySelector(
          ".e-date-header.e-navigate"
        );
        if (dateHeaderWrap) {
          dateHeaderWrap.innerHTML = `${month}/${day}`;
        }
      }
    },
    onEventRendered,
    onCellClick,
    onCellDoubleClick,
    resetMeetingObject,
    createMeeting,
    closeMeetingDialog,
    moreEventsClick,
    onPopupOpen,
    onDragStop,
    updateMeeting,
    openMenu,
    openQuickInfoDevicePopup,
    onDeleteMeeting,
    signalRInit,
    mailSendConfirmation,
    onEmailDialogCancel,
    onEmailSend,
    setScheduleTimes,
    adjustStartTime,
    setEndTime,
    findMeetingType,
    processAttendees,
    getTimeDetails,
    updateMeetingTypeColor,
    formatTime(date) {
      return new Date(date).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    openManual() {
      this.manualClick += 1;
      let height = screen.height;
      let width = screen.width;
      let strWindowFeatures =
        "location=no,toolbar=no,menubar=no,height=" +
        height +
        ",width=" +
        width +
        ",scrollbars=auto,fullscreen=yes";
      let routeData = this.$router.resolve({
        path: "/usermanual",
        query: {
          content:
            this.$i18n.locale == "en"
              ? "Manual_E_Calendar.pdf"
              : "Manual_J_Calendar.pdf",
          id: this.$route.name,
        },
      });
      let popup;
      if (
        routeData &&
        (typeof this.manualWindow === "undefined" ||
          this.manualWindow.closed ||
          this.manualClick === 1)
      ) {
        popup = window.open(routeData.href, "_blank", strWindowFeatures);
        this.manualWindow = popup;
        popup.moveTo(0, 0);
      } else {
        this.manualWindow.focus();
      }
    },
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    getTextColor(color) {
      return customFunctions.getContrast(color) ? "white" : "#424242";
    },
    onEditSelection,
    onCopyMeeting,
    onExport,
    onNavigating,
    getCurrentMonthYear,
    getMonthYearParams,
    onCreateMeeting,
  },
};

function onCreateMeeting() {
  this.meetingFormData = this.resetMeetingObject();
  this.isSeriesEdit = true;
  this.dialogMeeting = true;
}

async function onNavigating(args) {
  this.$nextTick(async () => {
    const filter = this.getCurrentMonthYear(args.currentDate);
    await this.getMeetingListApi(filter);
  });
}

async function updateMeetingTypeColor() {
  const tenantConfigList = await tenantConfigService.getUpdatedTenantConfigList();
  const meetingTypeConfig = tenantConfigList.find(
    (config) => config.configname === "meetingType"
  );

  this.updatedMeetingTypeList = meetingTypeConfig
    ? meetingTypeConfig.meetingType
    : [];
  
}

function getCurrentMonthYear(currentDate) {
  const { currentView, selectedMonth, selectedYear, from, to } =
    this.getMonthYearParams(currentDate);

  let filter = {};
  if (currentView !== "Year") {
    filter = {
      filter_type: "custom",
      from,
      to,
    };
  } else {
    filter = {
      filter_type: "yearly",
      year: String(selectedYear),
    };
  }
  return filter;
}

async function onExport() {
  const { currentView, selectedMonth, selectedYear, from, to } =
    this.getMonthYearParams(this.selectedDate);

  const params =
    currentView === "Year" ? { year: String(selectedYear) } : { from, to };

  try {
    const result = await this.exportIcs(params);
    downloadBase64AsICS(result.data, "meetings.ics");
  } catch (error) {
    console.error("Error exporting ICS:", error);
  }
}

function getMonthYearParams(currentDate) {
  const date = new Date(currentDate);
  const scheduleObj = this.$refs.ScheduleObj;
  const currentView = scheduleObj.ej2Instances.currentView;

  const renderDates = scheduleObj.ej2Instances.activeView.renderDates;
  const selectedMonth = date.getMonth() + 1;
  const selectedYear = date.getFullYear();
  const from = formatDate(new Date(renderDates[0]), "yyyy-MM-dd");
  const to = formatDate(
    new Date(renderDates[renderDates.length - 1]),
    "yyyy-MM-dd"
  );
  return { currentView, selectedMonth, selectedYear, from, to };
}

function downloadBase64AsICS(base64String, fileName) {
  const decodedData = atob(base64String);
  const uint8Array = new Uint8Array(decodedData.length);
  for (let i = 0; i < decodedData.length; i++) {
    uint8Array[i] = decodedData.charCodeAt(i);
  }
  const utfDecodedText = new TextDecoder("utf-8").decode(uint8Array);
  const blob = new Blob([utfDecodedText], { type: "text/calendar" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
}

function getTimeDetails(mDetails) {
  const Xmas95 = new Date(mDetails.scheduletStartTimeInUTC);
  const endDate = new Date(mDetails.scheduledEndTimeInUTC);
  const startTime = new Intl.DateTimeFormat(this.selectedLang, {
    timeStyle: "short",
  }).format(Xmas95);
  const endTime = new Intl.DateTimeFormat(this.selectedLang, {
    timeStyle: "short",
  }).format(endDate);

  return `${startTime} - ${endTime}`;
}

function onEditSelection(action) {
  this.isSeriesEdit = action;
  this.dialogMeeting = true;
  this.isIpadQuickInfoView = false;
  // this.onClose();
}

function onCopyMeeting(action) {
  if (this.meetingFormData && this.meetingFormData.attendeNote) {
    delete this.meetingFormData.attendeNote;
  }
  
  if (this.meetingFormData && this.meetingFormData.attendees) {
    this.meetingFormData.attendees = this.meetingFormData.attendees.map(attendee => {
      const { attendeNote, ...rest } = attendee;
      return rest;
    });
  }
  this.isSeriesEdit = false;
  this.dialogMeeting = true;
  this.isIpadQuickInfoView = false;
  this.isCopyMeeting = true;
}

function signalRInit(target) {
  this.signalConnection.on(target, async (message) => {
    if (message) {
      let loader;
      try {
        loader = this.$localLoader.show();
        const filter = await this.getCurrentMonthYear(this.selectedDate);
        await this.getMeetingListApi(filter);
        this.$localLoader.hide(loader);
      } catch (error) {
        this.$localLoader.hide(loader);
      }
    }
  });

  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

async function onDeleteMeeting(meeting) {
  let loader;
  try {
    if (meeting.attendees.length) {
      loader = this.$localLoader.show();
      const attendees = await this.processAttendees(meeting.attendees);
      meeting.attendees = attendees;
      this.meetingObject = meeting;
      this.showEmailDialog = true;
      this.$localLoader.hide(loader);
    }
    this.showMessageSheet(this.$t("agenda.calMeetingDeleted"), "success");
  } catch (error) {
    this.$localLoader.hide(loader);
  }
}

async function processAttendees(attendees) {
  let localUserList = [...this.userList];
  const userWithIdExists = (id) => localUserList.some((obj) => obj.id === id);
  const unmatchedObjects = attendees.filter((obj) => !userWithIdExists(obj.id));
  const unmatchedIds = unmatchedObjects.map((obj) => obj.id);

  if (unmatchedIds.length) {
    const result = await this.getDeletedUsersList(unmatchedIds);
    if (result.length) {
      localUserList = localUserList.concat(result);
    }
  }
  return attendees.map((item) => {
    const uData = localUserList.find((u) => u.id == item.id);
    return {
      id: item.id,
      email: item.email,
      attendedState: item.attendedState,
      title: item.title ? item.title : (uData.title || ""),
      firstName: item.firstName ? item.firstName : (uData.firstName || ""),
      lastName: item.lastName ? item.lastName : (uData.lastName || ""),
      profilePhoto: uData ? uData.profilePhoto : "",
    };
  });
}

async function mailSendConfirmation(meeting) {
  this.emailData = {
    open: true,
    type: "DeleteMeeting",
    meetingDetails: meeting,
    newDocuments: [],
    isPersonalised: true,
  };
  this.notificationMessage = this.$t("msgMeetingDeletionSuccess", {
    meetingName: meeting.meetingName,
  });
}

function onEmailDialogCancel() {
  this.emailData = {};
}

function onEmailSend(isSuccess) {
  this.emailData = {};
  if (isSuccess) {
    this.showMessageSheet(this.$t("agenda.SendEmailNotification"), "success");
  } else {
    this.showMessageSheet(this.$t("agenda.FaildEmailNotification"), "error");
  }
}

function openMenu() {
  this.openMeetingTypeMenu = true;
}

function createMeeting(event) {
  this.meetingFormData = this.resetMeetingObject();
  const startDate = new Date(event.startTime || event.data.StartTime);
  this.setScheduleTimes(startDate);
  this.isSeriesEdit = true;
  this.dialogMeeting = true;
}

function setScheduleTimes(startDate) {
  let date = new Date();
  this.meetingFormData.scheduletStartTimeInUTC = new Date(
    startDate
  ).toISOString();

  if (date.toDateString() === startDate.toDateString()) {
    this.adjustStartTime(date);
  } else {
    this.setEndTime(startDate);
  }
}

function adjustStartTime(date) {
  const currentHours = date.getHours();
  const currentMinutes = date.getMinutes();
  const startDate = new Date(date);
  startDate.setHours(currentHours);
  startDate.setMinutes(currentMinutes);
  const endDate = new Date(startDate.getTime() + 120 * 60 * 1000);
  this.meetingFormData.scheduletStartTimeInUTC = startDate.toISOString();
  this.meetingFormData.scheduledEndTimeInUTC = endDate.toISOString();
}

function setEndTime(startDate) {
  const endDate = new Date(startDate.getTime() + 120 * 60 * 1000);
  this.meetingFormData.scheduledEndTimeInUTC = endDate.toISOString();
}

function closeMeetingDialog() {
  this.dialogMeeting = false;
}

function onCellDoubleClick(event) {
  if (Boolean(this.meetingTypes.length)) {
    this.createMeeting(event);
  }
  event.cancel = true;
}

function onCellClick(event) {
  event.cancel = true;
}

function moreEventsClick(event) {
  //todo
}

function onDragStop(event) {
  if (event.name === "dragStop") {
    this.updateMeeting(event.data);
  }
}

async function updateMeeting(meetingData) {
  const meetingFormData = Object.assign({}, meetingData);
  meetingFormData.scheduletStartTimeInUTC = new Date(
    meetingData.StartTime
  ).toISOString();
  meetingFormData.scheduledEndTimeInUTC = new Date(
    meetingData.EndTime
  ).toISOString();

  if (meetingFormData.id) {
    meetingFormData.meetingType = meetingFormData.meetingType.label || "";
    await this.updateMeetingData(meetingFormData);
  }
}

async function onPopupOpen(event) {
  this.isIpadQuickInfoView = false;
  if (event.type === "Editor") {
    await handleEditorEvent.call(this, event);
  }

  if (event.type === "ViewEventInfo" || event.type === "QuickInfo") {
    event.cancel = true;
    await handleViewEventInfo.call(this, event);
  }

  if (event.type === "EventContainer" && hasMorePopupWrapper()) {
    event.cancel = true;
    handleEventContainer.call(this, event);
  }
}

async function handleEditorEvent(event) {
  if (event.data.id) {
    this.$router.push(
      "/meeting/" + event.data.id + "/calendar/tenant/" + this.tenantId
    );
  } else {
    if (Boolean(this.meetingTypes.length)) {
      this.createMeeting(event);
    }
  }
  event.cancel = true;
}

async function handleViewEventInfo(event) {
  try {
    let meeting = await this.getMeetingDetails(event.data.id);

    const moreCloseElm = document.querySelector(".e-more-event-close");
    if (moreCloseElm) {
      moreCloseElm.click();
    }
    const meetingFormData = {
      ...meeting,
      Location: meeting.location,
      Subject: meeting.meetingName,
      StartTime: new Date(meeting.scheduletStartTimeInUTC),
      EndTime: new Date(meeting.scheduledEndTimeInUTC),
      meetingType: this.findMeetingType(meeting.meetingType),
      Description: meeting.meetingType,
    };
    this.isCopyMeeting = false;

    Object.assign(this, {
      meetingFormData,
      isIpadQuickInfoView: true,
      showIpadInfoDialog: true,
    });

    event.cancel = true;
  } catch (error) {}
}

function findMeetingType(type) {
  const lowerType = String(type).toLowerCase();
  return (
    this.meetingTypesList.find(
      (c) => String(c.label).toLowerCase() === lowerType
    ) || {
      label: type,
      color: "#F5CBA7",
    }
  );
}

function hasMorePopupWrapper() {
  return document.getElementsByClassName("e-more-popup-wrapper e-device")
    .length;
}

function handleEventContainer(event) {
  this.moreEventData = event.data;
  this.showMoreEventDialog = true;
  event.cancel = true;
}

async function openQuickInfoDevicePopup(data) {
  try {
    this.isIpadQuickInfoView = true;
    let meeting = await this.getMeetingDetails(data.id);
    this.meetingFormData = meeting;
    this.showIpadInfoDialog = true;
  } catch (error) {}
}

function onEventRendered(args) {
  const meetingData = args.data;
  let backgroundColor = "#004c7a"; // Default color

  // Try to get color from meeting type
  if (meetingData.meetingType) {
    backgroundColor = this.getMeetingTypeColor(meetingData.meetingType);
  } else if (meetingData.Description) {
    backgroundColor = this.getMeetingTypeColor(meetingData.Description);
  }

  // Apply the background color
  args.element.style.backgroundColor = backgroundColor;

  // Optional: Set text color based on background for better contrast
  const textColor = this.getTextColor(backgroundColor);
  args.element.style.color = textColor;
}

function gettersMap() {
  return mapGetters({
    meetingTypeStoreList: "tenant/agendaMeetingTypes",
    tenantSettingTypes: "tenant/tenantSettingTypes",
    tenantSettingConfig: "tenant/tenantSettingConfig",
  });
}

function resetMeetingObject() {
  return {
    id: "",
    tenantid: sessionStorage.getItem("tenantId"),
    meetingName: "",
    meetingType: "",
    resolutionType: "",
    scheduletStartTimeInUTC: new Date().toISOString(),
    scheduledEndTimeInUTC: null,
    actualStartTimeInUTC: null,
    actualEndTimeInUTC: null,
    description: "",
    location: "",
    topics: [],
    documents: null,
    attendees: [],
    has_past_meetings: false,
    recurrenceData: {
      isMeetingRecurrence: false,
      recurrence_title: "",
      recurrenceStartDate: new Date().toISOString(),
      recurrenceEndDate: null,
      recurrencePattern: "month",
      repeatInterval: 1,
      selectedDays: [],
      recurrenceOnDay: {
        selectedValue: 0,
        day: new Date().getDate(),
        selectedWeekSlot: "first",
        selectedWeekDay: "sunday",
        selectedMonth: new Date().getMonth(),
      },
    },
  };
}
</script>

<style scoped>
::v-deep .container--fluid {
  padding: 0px !important;
}

::v-deep .v-card__text {
  width: 100%;
  padding: 12px !important;
}

/* ::v-deep .e-more-popup-wrapper .e-appointment .e-subject {
  color: #424242 !important;
} */
::v-deep .quick-info-header {
  /* background-color: white; */
  padding: 8px 18px;
}

::v-deep .quick-info-header-content {
  justify-content: flex-end;
  display: flex;
  flex-direction: column;
  padding: 5px 10px 5px;
}

::v-deep .quick-info-title {
  font-weight: 500;
  font-size: 16px;
  letter-spacing: 0.48px;
  height: 22px;
}

::v-deep .duration-text {
  font-size: 11px;
  letter-spacing: 0.33px;
  height: 14px;
}

::v-deep .content-area {
  margin: 0;
  padding: 10px;
  width: auto;
}

::v-deep .event-content {
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 15px;
}

::v-deep .meeting-type-wrap,
.meeting-subject-wrap,
.notes-wrap {
  font-size: 11px;
  color: #666;
  letter-spacing: 0.33px;
  height: 24px;
  padding: 5px;
}

::v-deep .event-content div label {
  display: inline-block;
  min-width: 45px;
  color: #666;
}

::v-deep .event-content div span {
  font-size: 11px;
  color: #151515;
  letter-spacing: 0.33px;
  line-height: 14px;
  padding-left: 8px;
}

::v-deep .cell-footer.e-btn {
  background-color: #ffffff;
  border-color: #878787;
  color: #878787;
}

::v-deep .cell-footer {
  padding-top: 10px;
}

::v-deep .e-quick-popup-wrapper {
  max-width: 510px !important;
}

::v-deep .e-quick-popup-wrapper .e-cell-popup .e-popup-content {
  padding: 0 14px;
}

::v-deep .e-quick-popup-wrapper .e-event-popup .e-popup-footer {
  display: block;
}

::v-deep .e-quick-popup-wrapper .e-popup-footer button:first-child {
  margin-right: 5px;
}

::v-deep .v-text-field--filled > .v-input__control > .v-input__slot,
.v-text-field--full-width > .v-input__control > .v-input__slot,
.v-text-field--outlined > .v-input__control > .v-input__slot {
  align-items: stretch;
  min-height: 28px;
  min-width: 250px;
}

::v-deep .v-input__control {
  display: flex;
  flex-direction: column;
  height: auto;
  flex-grow: 1;
  flex-wrap: wrap;
  max-width: 100%;
}

::v-deep .v-autocomplete {
  min-width: 0px;
}

::v-deep .e-more-event-content .e-appointment .e-time {
  display: none;
}

::v-deep .e-more-event-content .e-appointment .e-subject span {
  display: none;
}

::v-deep .v-autocomplete.v-select.v-input input {
  min-width: 0px;
  width: 0px;
}

::v-deep .v-autocomplete.v-select.v-input--is-focused input {
  min-width: 0px;
}

::v-deep .v-text-field--enclosed .v-input__append-inner,
.v-text-field--enclosed .v-input__append-outer {
  margin-top: 3px;
}

::v-deep .searchBar {
  max-height: 28px !important;
}

.ejschedule /deep/ .e-month-view .e-work-cells {
  height: 93px !important;
}

.ejschedule /deep/ .e-month-view .e-more-indicator {
  color: var(--v-primaryDark2-base);
  font-size: 12px;
  font-weight: bold;
}

.e-schedule {
  min-height: 400px !important;
  border: none;
  background-color: transparent;
}

.ejschedule /deep/ .e-agenda-view .e-subject {
  max-width: 90%;
}

.ejschedule /deep/ .e-agenda-view .e-subject,
.ejschedule /deep/ .e-agenda-view .e-location,
.ejschedule /deep/ .e-agenda-view .e-date-time {
  color: #ffffff !important;
}

.ejschedule /deep/ .e-agenda-view .e-appointment:hover .e-subject,
.ejschedule
  /deep/
  .e-agenda-view
  .e-appointment.e-appointment-border.e-active-appointment-agenda
  .e-subject {
  color: #000000de !important;
}

.ejschedule /deep/ .e-agenda-view .e-appointment:hover .e-location,
.ejschedule /deep/ .e-agenda-view .e-appointment:hover .e-date-time,
.ejschedule
  /deep/
  .e-agenda-view
  .e-appointment.e-appointment-border.e-active-appointment-agenda
  .e-location,
.ejschedule
  /deep/
  .e-agenda-view
  .e-appointment.e-appointment-border.e-active-appointment-agenda
  .e-date-time {
  color: #0000008a !important;
}

.e-schedule .e-agenda-view .e-appointment:hover {
  background: none !important;
}

.e-schedule .e-agenda-view .e-appointment.e-appointment-border,
.e-schedule .e-agenda-view .e-appointment:focus {
  background: none !important;
}

::v-deep .e-header-date {
  color: rgba(0, 0, 0, 0.87);
  font-size: 18px;
  line-height: 1;
  max-width: 100% !important;
}

::v-deep .e-schedule .e-schedule-table {
  border: 1px solid lightgray;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  table-layout: fixed;
  width: 100%;
}

::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  td {
  position: relative;
  height: 40px;
  vertical-align: top;
}

::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  td
  span.e-day {
  position: absolute;
  z-index: 1;
}

::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  .e-appointment {
  background-color: var(--v-pageHeader-base) !important;
  border-radius: 50%;
  height: 30px;
  position: relative;
  width: 30px;
  margin: 0px;
}

::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  td
  span.e-day:not(:last-child) {
  color: #fff !important;
}

::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  td
  .e-appointment {
  opacity: 0.9;
  transition: opacity 100ms linear;
  /* background-color: initial !important;
    border: 1px solid #3f51b5;
    box-shadow: inset 0 0 0 2px #fff; */
}

::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  .e-selected
  .e-appointment {
  opacity: 1;
  /*transition: opacity 100ms linear;*/
  border: none;
  background-color: inherit !important;
}

::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  td:focus
  span.e-day,
::v-deep
  .e-schedule
  .e-year-view
  .e-calendar-wrapper
  .e-month-calendar.e-calendar
  td:hover
  span.e-day {
  background-color: var(--v-pageHeader-lighten2) !important;
}

::v-deep .e-more-appointment-wrapper .e-appointment {
  opacity: 1;
}

::v-deep .e-toolbar {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  background-color: #dddddd !important;
  border: 1px none rgba(0, 0, 0, 0.12);
  box-shadow: none;
  height: 56px !important;
}

::v-deep .e-schedule .e-schedule-toolbar .e-toolbar-items.e-tbar-pos {
  height: inherit !important;
  min-height: 42px;
}

::v-deep .e-toolbar .e-toolbar-items {
  background: #dddddd !important;
  color: #151515 !important;
}

::v-deep.e-schedule .e-month-view .e-current-date .e-date-header,
.e-schedule .e-month-agenda-view .e-current-date .e-date-header {
  background-color: #e3165b;
  border-radius: 50%;
  color: #fff;
  width: 30px;
  height: 30px;
  line-height: 30px;
}
</style>
